import { Injectable, NgZone } from '@angular/core';
import { Observable, BehaviorSubject, Subject, of, concat, timer } from 'rxjs';
import { tap, finalize, shareReplay } from 'rxjs/operators';
import { GlobalService } from './global.service';

export interface CrmCacheEntry<T = any> {
  data: T;
  updatedAt: number;
}

export interface CrmSwrOptions {
  /** Forçar atualização ignorando cache inicial */
  forceRefresh?: boolean;
  /** Tempo (ms) durante o qual os dados são considerados 100% frescos sem necessidade de revalidar (default: 45s) */
  freshTtlMs?: number;
  /** Tempo máximo (ms) de validade em localStorage (default: 7 dias) */
  maxAgeMs?: number;
  /** Persistir em localStorage para arranque instantâneo entre sessões (default: true) */
  persist?: boolean;
  /** Se true, trata-se de um prefetch silencioso e não altera o timestamp visível da página ativa */
  silentPrefetch?: boolean;
}

export interface CrmFreshnessState {
  /** Timestamp (ms) dos dados atualmente visíveis no ecrã */
  updatedAt: number | null;
  /** True enquanto está a revalidar silenciosamente em background */
  isRevalidating: boolean;
  /** True quando os dados apresentados vieram da cache ou têm > 60s */
  isStaleOrCached: boolean;
  /** Texto ultra-discreto pronto a exibir (ex: "Dados de há 4 min") */
  discreetLabel: string;
  /** Tooltip detalhado com data/hora exata */
  fullTimestampTooltip: string;
}

const STORAGE_PREFIX = 'sv_crm_swr_v3_';
const DEFAULT_FRESH_TTL_MS = 45 * 1000; // 45 segundos
const DEFAULT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 dias

@Injectable({
  providedIn: 'root',
})
export class CrmCacheService {
  private memoryCache = new Map<string, CrmCacheEntry>();
  private inFlightRequests = new Map<string, Observable<any>>();
  private activeRevalidations = 0;

  private _updatedAt$ = new BehaviorSubject<number | null>(null);
  private _isRevalidating$ = new BehaviorSubject<boolean>(false);
  private _usedCacheOnCurrentView$ = new BehaviorSubject<boolean>(false);
  private _freshnessState$ = new BehaviorSubject<CrmFreshnessState>({
    updatedAt: null,
    isRevalidating: false,
    isStaleOrCached: false,
    discreetLabel: '',
    fullTimestampTooltip: '',
  });

  /** Evento emitido quando o utilizador clica no indicador discreto para forçar atualização */
  public readonly forceRefreshRequested$ = new Subject<void>();

  /** Observable reativo com o estado de frescura dos dados para a UI */
  public readonly freshnessState$ = this._freshnessState$.asObservable();

  constructor(
    private globalService: GlobalService,
    private ngZone: NgZone,
  ) {
    // Atualizar o texto relativo ("há X min") a cada 15 segundos fora da Angular Zone para zero impacto de performance
    if (typeof window !== 'undefined') {
      this.ngZone.runOutsideAngular(() => {
        timer(15000, 15000).subscribe(() => {
          const current = this._freshnessState$.value;
          const next = this.computeFreshnessState(
            this._updatedAt$.value,
            this._isRevalidating$.value,
            this._usedCacheOnCurrentView$.value,
          );
          if (
            next.discreetLabel !== current.discreetLabel ||
            next.isStaleOrCached !== current.isStaleOrCached ||
            next.isRevalidating !== current.isRevalidating
          ) {
            this.ngZone.run(() => {
              this._freshnessState$.next(next);
            });
          }
        });
      });
    }
  }

  /**
   * Gera um identificador único de escopo (utilizador + mediador/admin) para isolar a cache.
   * Persiste o último escopo autenticado para garantir hit imediato (0ms) no arranque da página antes de restoreSession terminar.
   */
  public getScopePrefix(): string {
    const user: any = this.globalService?.loggedUserClass;
    const idUser = user?.idUser || 0;
    const nif = String(user?.customerClass?.NIF || user?.NIF || user?.commercialClass?.NIF || '').trim();
    const idComm = user?.commercialClass?.idCommercial || user?.idCommercial || 0;
    const isMediadorProfile = Boolean(
      nif === '212915665' || (idComm > 1 && !user?.commercialClass?.Default && !user?.Admin && !user?.SiteManagement),
    );
    const pathname = typeof window !== 'undefined' ? window.location?.pathname || '' : '';
    const isMedRoute =
      pathname.includes('/crm-mediador') ||
      (pathname.startsWith('/crm') && !pathname.startsWith('/crm/pedidos') && isMediadorProfile)
        ? 'med'
        : 'crm';

    if (idUser > 0 || nif !== '' || idComm > 0) {
      const activeScope = `${isMedRoute}_u${idUser}_c${idComm}_${nif || 'nonif'}`;
      if (typeof window !== 'undefined' && window.localStorage) {
        try {
          window.localStorage.setItem(`sv_crm_last_scope_${isMedRoute}`, activeScope);
        } catch {
          // ignore
        }
      }
      return activeScope;
    }

    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        const lastScope = window.localStorage.getItem(`sv_crm_last_scope_${isMedRoute}`);
        if (lastScope) {
          return lastScope;
        }
      } catch {
        // ignore
      }
    }

    return `${isMedRoute}_u0_c0_nonif`;
  }

  private buildFullKey(key: string): string {
    return `${this.getScopePrefix()}::${key}`;
  }

  /**
   * Leitura síncrona imediata (0ms) do valor em cache sem efeitos secundários
   */
  public peek<T>(key: string, maxAgeMs = DEFAULT_MAX_AGE_MS): T | null {
    const entry = this.get<T>(key, maxAgeMs);
    return entry ? entry.data : null;
  }

  /**
   * Obtém entrada da cache (Memória -> localStorage)
   */
  public get<T>(key: string, maxAgeMs = DEFAULT_MAX_AGE_MS): CrmCacheEntry<T> | null {
    const fullKey = this.buildFullKey(key);
    const now = Date.now();

    const mem = this.memoryCache.get(fullKey);
    if (mem) {
      if (now - mem.updatedAt <= maxAgeMs) {
        return mem as CrmCacheEntry<T>;
      }
      this.memoryCache.delete(fullKey);
    }

    if (typeof window === 'undefined' || !window.localStorage) {
      return null;
    }

    try {
      const raw = window.localStorage.getItem(STORAGE_PREFIX + fullKey);
      if (!raw) return null;
      const parsed: CrmCacheEntry<T> = JSON.parse(raw);
      if (parsed && typeof parsed.updatedAt === 'number' && now - parsed.updatedAt <= maxAgeMs) {
        this.memoryCache.set(fullKey, parsed);
        return parsed;
      }
      window.localStorage.removeItem(STORAGE_PREFIX + fullKey);
    } catch {
      // Ignorar erros de parse/storage
    }
    return null;
  }

  /**
   * Guarda entrada na cache (Memória + localStorage seguro)
   */
  public set<T>(key: string, data: T, persist = true): CrmCacheEntry<T> {
    const fullKey = this.buildFullKey(key);
    const entry: CrmCacheEntry<T> = {
      data,
      updatedAt: Date.now(),
    };
    this.memoryCache.set(fullKey, entry);

    if (persist && typeof window !== 'undefined' && window.localStorage) {
      try {
        const serialized = JSON.stringify(entry);
        // Evitar guardar payloads gigantes (> 350KB) no localStorage; mantê-los apenas em memória
        if (serialized.length <= 350_000) {
          window.localStorage.setItem(STORAGE_PREFIX + fullKey, serialized);
        }
      } catch {
        this.pruneLocalStorage();
      }
    }

    return entry;
  }

  /**
   * Padrão Stale-While-Revalidate (SWR):
   * - Se existir em cache e !forceRefresh: emite IMEDIATAMENTE (0ms) os dados em cache.
   * - Se os dados tiverem mais de `freshTtlMs` (45s), dispara em simultâneo o pedido de rede em background
   *   e emite os dados atualizados assim que chegarem, sem bloquear a UI.
   */
  public swr<T>(
    key: string,
    fetchFn: (forceRefresh: boolean) => Observable<T>,
    options: CrmSwrOptions = {},
  ): Observable<T> {
    const {
      forceRefresh = false,
      freshTtlMs = DEFAULT_FRESH_TTL_MS,
      maxAgeMs = DEFAULT_MAX_AGE_MS,
      persist = true,
      silentPrefetch = false,
    } = options;

    const fullKey = this.buildFullKey(key);
    const cached = !forceRefresh ? this.get<T>(key, maxAgeMs) : null;
    const now = Date.now();

    const createNetworkStream = (isBackground: boolean): Observable<T> => {
      let existingInFlight = this.inFlightRequests.get(fullKey);
      if (existingInFlight && !forceRefresh) {
        return existingInFlight as Observable<T>;
      }

      if (!silentPrefetch && isBackground) {
        this.incrementRevalidating();
      }

      const net$ = fetchFn(forceRefresh || isBackground).pipe(
        tap(freshData => {
          const saved = this.set(key, freshData, persist);
          if (!silentPrefetch) {
            this._usedCacheOnCurrentView$.next(false);
            this.updateTimestamp(saved.updatedAt);
          }
        }),
        finalize(() => {
          this.inFlightRequests.delete(fullKey);
          if (!silentPrefetch && isBackground) {
            this.decrementRevalidating();
          }
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

      this.inFlightRequests.set(fullKey, net$);
      return net$;
    };

    const hasNonEmptyCache =
      cached !== null && cached !== undefined && !(Array.isArray(cached.data) && cached.data.length === 0);

    if (hasNonEmptyCache && cached) {
      const age = now - cached.updatedAt;
      if (!silentPrefetch) {
        // Se os dados em cache têm mais de 20s, marcamos que vieram de cache para mostrar a nota discreta
        if (age > 20_000) {
          this._usedCacheOnCurrentView$.next(true);
        }
        this.updateTimestamp(cached.updatedAt);
      }

      // Se a cache é muito recente (< freshTtlMs), devolve imediatamente e completa (0ms)
      if (age <= freshTtlMs) {
        return of(cached.data);
      }

      // Cache existe mas já passou o freshTtlMs:
      // Emite imediatamente os dados em cache (0ms) E concatena a atualização em background!
      return concat(of(cached.data), createNetworkStream(true));
    }

    // Sem cache ou forceRefresh solicitado
    return createNetworkStream(false);
  }

  /**
   * Invalida uma chave específica da cache (Memória + inFlight + localStorage)
   */
  public invalidate(key: string): void {
    const fullKey = this.buildFullKey(key);
    this.memoryCache.delete(fullKey);
    this.inFlightRequests.delete(fullKey);
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        window.localStorage.removeItem(STORAGE_PREFIX + fullKey);
      } catch {
        // Ignorar
      }
    }
  }

  /**
   * Invalida toda a cache do escopo atual (ex: após criar/editar registo ou forçar refresh global)
   */
  public invalidateCurrentScope(): void {
    const prefix = this.getScopePrefix() + '::';
    for (const k of Array.from(this.memoryCache.keys())) {
      if (k.startsWith(prefix)) {
        this.memoryCache.delete(k);
      }
    }
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        const storagePrefix = STORAGE_PREFIX + prefix;
        for (let i = window.localStorage.length - 1; i >= 0; i--) {
          const k = window.localStorage.key(i);
          if (k && k.startsWith(storagePrefix)) {
            window.localStorage.removeItem(k);
          }
        }
      } catch {
        // Ignorar
      }
    }
  }

  /**
   * Dispara atualização forçada a partir do botão/indicador discreto
   */
  public requestForceRefresh(): void {
    this.forceRefreshRequested$.next();
  }

  private incrementRevalidating(): void {
    this.activeRevalidations++;
    this._isRevalidating$.next(true);
    this.emitFreshnessState();
  }

  private decrementRevalidating(): void {
    this.activeRevalidations = Math.max(0, this.activeRevalidations - 1);
    if (this.activeRevalidations === 0) {
      this._isRevalidating$.next(false);
    }
    this.emitFreshnessState();
  }

  private updateTimestamp(ts: number): void {
    const current = this._updatedAt$.value;
    // Se estamos a mostrar dados da cache, manter o timestamp mais antigo da vista para ser fiel à idade dos dados;
    // quando termina de atualizar com dados novos, avança para o novo timestamp
    if (!current || ts >= current || this._usedCacheOnCurrentView$.value) {
      this._updatedAt$.next(ts);
    }
    this.emitFreshnessState();
  }

  private emitFreshnessState(): void {
    this._freshnessState$.next(
      this.computeFreshnessState(
        this._updatedAt$.value,
        this._isRevalidating$.value,
        this._usedCacheOnCurrentView$.value,
      ),
    );
  }

  private computeFreshnessState(
    updatedAt: number | null,
    isRevalidating: boolean,
    usedCache: boolean,
  ): CrmFreshnessState {
    if (!updatedAt) {
      return {
        updatedAt: null,
        isRevalidating,
        isStaleOrCached: false,
        discreetLabel: '',
        fullTimestampTooltip: '',
      };
    }

    const now = Date.now();
    const diffSec = Math.max(0, Math.floor((now - updatedAt) / 1000));
    const dateObj = new Date(updatedAt);
    const hh = String(dateObj.getHours()).padStart(2, '0');
    const mm = String(dateObj.getMinutes()).padStart(2, '0');
    const dd = String(dateObj.getDate()).padStart(2, '0');
    const mo = String(dateObj.getMonth() + 1).padStart(2, '0');

    const isToday = new Date(now).toDateString() === dateObj.toDateString();
    const timeStr = `${hh}:${mm}`;
    const fullTooltip = `Dados de ${dd}/${mo}/${dateObj.getFullYear()} às ${timeStr}${
      isRevalidating ? ' · A sincronizar em segundo plano...' : ' · Clique para atualizar agora'
    }`;

    // Consideramos "não atualizado / em cache" se:
    // - Está a sincronizar em background (isRevalidating)
    // - Ou veio de cache com mais de 20s (usedCache)
    // - Ou já passaram mais de 60 segundos desde a última leitura
    const isStaleOrCached = isRevalidating || usedCache || diffSec >= 60;

    let discreetLabel = '';
    if (diffSec < 45) {
      discreetLabel = isRevalidating ? `Dados das ${timeStr} · a atualizar…` : `Dados das ${timeStr}`;
    } else if (diffSec < 3600) {
      const mins = Math.max(1, Math.floor(diffSec / 60));
      discreetLabel = isRevalidating
        ? `Dados de há ${mins} min · a atualizar…`
        : `Dados de há ${mins} min (${timeStr})`;
    } else if (isToday) {
      discreetLabel = isRevalidating ? `Dados de hoje às ${timeStr} · a atualizar…` : `Dados de hoje às ${timeStr}`;
    } else {
      discreetLabel = isRevalidating
        ? `Dados de ${dd}/${mo} às ${timeStr} · a atualizar…`
        : `Dados de ${dd}/${mo} às ${timeStr}`;
    }

    return {
      updatedAt,
      isRevalidating,
      isStaleOrCached,
      discreetLabel,
      fullTimestampTooltip: fullTooltip,
    };
  }

  private pruneLocalStorage(): void {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
      const entries: { key: string; updatedAt: number }[] = [];
      for (let i = 0; i < window.localStorage.length; i++) {
        const k = window.localStorage.key(i);
        if (k && k.startsWith(STORAGE_PREFIX)) {
          try {
            const parsed = JSON.parse(window.localStorage.getItem(k) || '{}');
            entries.push({ key: k, updatedAt: parsed.updatedAt || 0 });
          } catch {
            entries.push({ key: k, updatedAt: 0 });
          }
        }
      }
      entries.sort((a, b) => a.updatedAt - b.updatedAt);
      const toRemove = Math.ceil(entries.length / 2);
      for (let i = 0; i < toRemove; i++) {
        window.localStorage.removeItem(entries[i].key);
      }
    } catch {
      // Ignorar
    }
  }
}
